import { createFileRoute } from '@tanstack/react-router';
import { Suspense, lazy } from 'react';
import { z } from 'zod';

import { PageSkeleton } from '~/components/common/page-skeleton/page-skeleton';
import type { CatalogValidationView } from '~/features/exports/catalog/components/catalog-validation-panel';
import { useCatalogExportType } from '~/features/exports/hooks/use-catalog-export-type';
import { useMainScrollReset } from '~/features/exports/hooks/use-main-scroll-reset';

// Explicit dynamic import: this page now also pulls in the export-profiles feature (the
// header's "Save as export profile" sheet), a module graph shared with several sibling
// routes under /exports/catalog/** — an explicit boundary keeps it split from the main bundle
// regardless of the router plugin's own code-splitting heuristic (see docs' performance notes).
const CatalogExportPage = lazy(() =>
  import('~/features/exports/catalog/pages/catalog-export-page').then((m) => ({
    default: m.CatalogExportPage,
  })),
);

/**
 * The connector and the validation panel's view live in the URL: the rest of the draft
 * (batches, pins, columns) is in `useCatalogDraftStore`, because Part 2's id lists outgrow a
 * URL. The panel's view does not — and in the URL, the browser's Back steps up through it.
 */
const searchSchema = z.object({
  connector: z.enum(['GMC', 'META']).optional(),
  validation: z.enum(['overview', 'exportProducts', 'batch', 'products']).optional(),
  validationBatch: z.string().min(1).optional(),
  /** Profile mode: edit this export profile's rows, then "Save to profile". */
  profile: z.string().min(1).optional(),
  /** "New export profile": pick the rows here, then "Save as export profile". */
  newProfile: z.boolean().optional(),
  /** The wizard step on screen (1 Batches … 4 Review); a validation deep link implies step 2.
   *  A bookmark from the five-step wizard (step 5) lands on Review. */
  step: z.coerce
    .number()
    .int()
    .min(1)
    .transform((n) => Math.min(n, 4))
    .optional()
    .catch(undefined),
  /** Products & validation's view (the grid, or the checks by batch). The five-step wizard's
   *  `versions` / `leftOut` tabs arrive here too; `RouteComponent` turns them into a rule. */
  view: z.enum(['products', 'validation', 'versions', 'leftOut']).optional().catch(undefined),
  /** The rule panel to open on Products & validation, e.g. from a profile's "Edit in builder". */
  rule: z
    .enum(['version', 'live', 'checks', 'rowRules', 'pins', 'leftOut'])
    .optional()
    .catch(undefined),
});

/** The URL's validation keys as the panel's view (`null` = closed, or a batch view without one). */
function toValidationView(
  validation: 'overview' | 'exportProducts' | 'batch' | 'products' | undefined,
  batchId: string | undefined,
): CatalogValidationView | null {
  if (validation === 'overview' || validation === 'exportProducts') return { view: validation };
  if (validation && batchId) return { view: validation, batchId };
  return null;
}

/**
 * Full-catalog export builder — open to every tenant user, like the batch export page. Lives at
 * `/exports/catalog/new` now that `/exports/catalog` itself is the Catalog exports list; this
 * route is UNGATED (unchanged from before the list existed) — same as the new list/run/profile
 * screens (see `../exports/$exportId.tsx`'s own docblock): a `catalog_export_profiles` feature
 * flag was added in an earlier pass and then removed, so nothing in this feature is gated behind
 * it today. The list's `index.tsx` redirects any old `/exports/catalog?validation=...` deep link
 * here, forwarding its search verbatim, so a bookmark into the validation panel still opens
 * exactly where it used to.
 */
export const Route = createFileRoute('/_authenticated/_tenant/exports/catalog/new')({
  validateSearch: searchSchema,
  component: RouteComponent,
});

function RouteComponent() {
  useCatalogExportType();
  useMainScrollReset();
  const {
    connector = 'GMC',
    validation,
    validationBatch,
    profile,
    newProfile,
    step,
    view,
    rule,
  } = Route.useSearch();
  const navigate = Route.useNavigate();
  // A five-step link to the old Versions / Left out tab (they lived on step 3, Explore products)
  // opens that rule on Products & validation; on any other step the stale tab is ignored.
  const fromOldProductsStep = step === undefined || step === 3;
  const legacyRule = !fromOldProductsStep
    ? undefined
    : view === 'versions'
      ? ('version' as const)
      : view === 'leftOut'
        ? ('leftOut' as const)
        : undefined;
  return (
    <Suspense fallback={<PageSkeleton />}>
      <CatalogExportPage
        connector={connector}
        onConnectorChange={(next) => {
          // A connector switch is a fresh page, but on the same wizard step.
          void navigate({ search: { connector: next, ...(step ? { step } : {}) }, replace: true });
        }}
        profileId={profile}
        startNewProfile={newProfile === true}
        step={legacyRule ? 2 : (step ?? (validation ? 2 : 1))}
        initialView={
          view === 'products' || view === 'validation'
            ? view
            : validation
              ? 'validation'
              : undefined
        }
        initialRule={rule ?? legacyRule}
        onStepChange={(next) => {
          // A history entry per step, so the browser's Back returns to the previous step.
          void navigate({ search: (prev) => ({ ...prev, step: next }) });
        }}
        validationView={toValidationView(validation, validationBatch)}
        onValidationViewChange={(next) => {
          // A history entry per step, so Back goes up a level; closing replaces the entry.
          void navigate({
            search: (prev) => ({
              ...prev,
              validation: next?.view,
              validationBatch: next && 'batchId' in next ? next.batchId : undefined,
            }),
            replace: next === null,
          });
        }}
      />
    </Suspense>
  );
}
